var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

/**
 * `webpack` & `rollup` can automagically split code imported using `import()`
 * into separate bundles. With this feature, we can create bundles for each
 * route, lazily loaded only if/when the user is about to enter the given route.
 *
 * Bundle splitting via dynamic `import()` is still highly experimental,
 * `webpack` and `rollup` each producing different output. We're assuming
 * `webpack` is used for the "final" build step (e.g. bundling an app) and
 * `rollup` is used for bundling libraries.
 *
 * The path provided to `import()` **cannot be fully dynamic** since webpack
 * needs to resolve it at compile time (see
 * https://webpack.js.org/api/module-methods/#import-). To retain flexibility,
 * we use a "token", replaced by webpack with the desired path using a
 * `ContextReplacementPlugin`. See `RouteComponentsContextReplacementPlugin` in
 * `./webpack`.
 *
 * @param path File path to the route component, relative to what the
 *             `upd-app-shell-route-components` token is replaced with.
 * @returns {Promise.<function(): string>}
 */

function loadComponent(_ref) {
  var definition = _ref.definition,
      _ref$args = _ref.args,
      args = _ref$args === undefined ? [] : _ref$args,
      dynamicComponentImport = _ref.dynamicComponentImport;

  switch (typeof definition === 'undefined' ? 'undefined' : _typeof(definition)) {
    case 'function':
      return Promise.resolve(definition.apply(undefined, _toConsumableArray(args)));

    case 'string':
      if (definition.length > 0) {
        return dynamicComponentImport(definition).then(function (Component) {
          return (
            // TODO: Webpack doesn't support es modules yet so must check for ".default".
            new (Function.prototype.bind.apply(Component.default || Component, [null].concat(_toConsumableArray(args))))()
          );
        });
      } else {
        // TODO: For now, allow passing empty string to the route config in order to clear an outlet.
        return Promise.resolve('');
      }

    default:
      return Promise.reject('The component definition must be a file path or a function.');
  }
}

var mountComponent = function mountComponent(target) {
  return function (component) {
    var node = !component.nodeType ? document.createTextNode(component) : component;

    target.innerHTML = '';
    target.appendChild(node);
  };
};

var resolvePath = function resolvePath(routes) {
  return function (path) {
    var result = Object.keys(routes).find(function (key) {
      return new RegExp('^' + key + '$').test(path);
    });

    return {
      // To retain flexibility, we don't make assumptions about the structure
      // of the config, but leave that up to the consumer.
      config: routes[result],

      // TODO: Returns route param values as an array, in order of which the
      // associated capturing groups appear in the route config regexp. Not
      // providing a param name to go with the value is crude. We might consider
      // one of the following options to support param names:
      // 1. Named capturing groups: https://github.com/tc39/proposal-regexp-named-groups
      // 2a. `matchit` library: https://github.com/lukeed/matchit
      // 2b. `path-to-regexp` library: https://github.com/pillarjs/path-to-regexp
      params: path.match(result).slice(1)
      // Throw away unmatched params
      .filter(function (param) {
        return param !== undefined;
      })
    };
  };
};

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var isValidRoute = function isValidRoute(route) {
  return typeof route === 'string' && route.length > 0;
};

var resolveRoute = function resolveRoute(router, store) {
  var resolve = resolvePath(router.config.routes);

  return function (path) {
    var previousPaths = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    var result = resolve(path);
    var _result$config = result.config;
    _result$config = _result$config === undefined ? {} : _result$config;
    var _result$config$redire = _result$config.redirects,
        redirects = _result$config$redire === undefined ? [] : _result$config$redire;


    return resolveRedirect(router, store, redirects).then(function (redirect) {
      // A closed loop occurs if two states redirect to each other.
      var isClosedLoop = previousPaths.indexOf(redirect) !== -1;

      if (redirect && !isClosedLoop) {
        return resolveRoute(router, store)(redirect, previousPaths.concat(path));
      } else {
        /* Rather than forcing an error when entering a closed loop, allowing a
         * redirect to resolve to the same route config can be useful, so long
         * as the loop isn't allowed to repeat infinitely.
         *
         * For example, a dynamic route that is boiled down to a single path
         * using redirects, where the redirect result matches the dynamic route
         * pattern that matched the original redirect.
         *
         * ```js
         * [`(/sign-up|/invite/${token}`]: {
         *   ...
         *   // An `/invite/${token}` URL would redirect to itself. The redirect
         *   // could protect against returning the same URL, but easier if the
         *   // router handles automatically.
         *   redirects: [inviteToken => inviteToken ? `/invite/${token}` : ''],
         * }
         * ```
         */
        return _extends({}, result, { path: path });
      }
    });
  };
};

/**
 * @param router
 * @param store
 * @param redirects
 * @return {Promise}
 */
var resolveRedirect = function resolveRedirect(router, store, redirects) {
  return redirects.reduce(function (result, redirect) {
    return result.then(function (result) {
      // Once we've found a valid redirect, skip the rest of redirects.
      return isValidRoute(result) ? Promise.resolve(result) : redirect(router, store);
    });
  }, Promise.resolve('')).then(function (result) {
    return isValidRoute(result) ? result : '';
  });
};

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var createRouter = function createRouter(_ref, store) {
  var _ref$basePath = _ref.basePath,
      basePath = _ref$basePath === undefined ? '' : _ref$basePath,
      dynamicComponentImport = _ref.dynamicComponentImport,
      element = _ref.element,
      _ref$mountFn = _ref.mountFn,
      mountFn = _ref$mountFn === undefined ? mountComponent : _ref$mountFn,
      routes = _ref.routes;

  var config = {
    basePath: basePath,
    element: element,
    mountFn: mountFn,
    routes: routes,
    dynamicComponentImport: dynamicComponentImport
  };

  var addBasePath = function addBasePath(path) {
    return basePath + path;
  };

  var resolve = function resolve(path) {
    return resolveRoute(router, store)(path).then(function (result) {
      if (!result.config) {
        throw new Error('Unable to resolve ' + path + ' to a route config.');
      }

      return result;
    });
  };

  var pushHistoryState = function pushHistoryState(state) {
    return history.pushState(null, '', addBasePath(state));
  };

  var loadCurrentPath = function loadCurrentPath() {
    return router.currentRoute.then(function (route) {
      var components = route.config.components,
          path = route.path;

      // The browser will have already added the "current path" to history.
      // Replace that entry if we were redirected.

      if (path !== router.currentPath) {
        history.replaceState(null, '', addBasePath(path));
      }

      return loadComponents(_extends$1({}, config, { router: router, store: store }), components).then(function () {
        return dispatchRouteChange(element, route);
      });
    });
  };

  var router = {
    config: config,
    get currentPath() {
      return window.location.pathname.replace(new RegExp('^' + basePath), '');
    },
    get currentRoute() {
      return resolve(router.currentPath);
    },
    init: function init() {
      window.addEventListener('popstate', loadCurrentPath);
      return loadCurrentPath();
    },
    destroy: function destroy() {
      window.removeEventListener('popstate', loadCurrentPath);
    },
    goTo: function goTo(desiredPath) {
      return resolve(desiredPath).then(function (route) {
        var components = route.config.components,
            path = route.path;

        // Don't add to history if `desiredPath` resolved to the current path.

        if (path !== router.currentPath) {
          try {
            pushHistoryState(path);
          } catch (ex) {
            try {
              // Try to self-heal from a chromium bug
              // https://bugs.chromium.org/p/chromium/issues/detail?id=918988
              __gCrWeb.message.getMessageQueue();
              pushHistoryState(path);
            } catch (ignore) {}
          }
        }

        return loadComponents(_extends$1({}, config, { router: router, store: store }), components).then(function () {
          return dispatchRouteChange(element, route);
        });
      });
    }
  };

  return router;
};

var loadComponents = function loadComponents(_ref2, components) {
  var element = _ref2.element,
      mountFn = _ref2.mountFn,
      router = _ref2.router,
      store = _ref2.store,
      dynamicComponentImport = _ref2.dynamicComponentImport;

  return Promise.all(Object.keys(components).map(function (outletSelector) {
    var componentDefinition = components[outletSelector];
    var outletElement = element.querySelector(outletSelector);

    if (!outletElement) {
      throw new Error('No element found matching "' + outletSelector + '".');
    }

    return loadComponent({
      definition: componentDefinition,
      dynamicComponentImport: dynamicComponentImport,
      args: [router, store]
    }).then(mountFn(outletElement), function (err) {
      throw new Error(outletSelector + ': ' + err);
    });
  }));
};

function dispatchRouteChange(element, route) {
  // If `element` is a `shadowRoot`, `dispatchEvent` won't work in ShadyDOM
  // because `element` is a `DocumentFragment`. Using `host` will work.
  // TODO: Remove. We should be able to dispatch from `element` directly.
  (element.host || element).dispatchEvent(new CustomEvent('routechange', {
    bubbles: true,
    composed: true,
    detail: route
  }));
}

export default createRouter;
export { resolveRoute, resolveRedirect };
